import { useCallback, useState } from 'react';
import type { CompleteMultipartUploadOutput } from '@aws-sdk/client-s3';
import { S3 } from '@aws-sdk/client-s3';
import { Upload } from '@aws-sdk/lib-storage';
import { useChatApiClient } from '../containers/Chat/useApiClient';

export type FormFileUploadStatus =
  | 'idle'
  | 'queuing'
  | 'uploading'
  | 'completing'
  | 'success'
  | 'error';

export type FormFileUploadState = {
  status: FormFileUploadStatus;
  /** Identifier returned by the backend after successful upload. Use this as the field value. */
  identifier: string | null;
  fileName: string | null;
  fileType: string | null;
  progress: number;
  error: string | null;
};

const INITIAL_STATE: FormFileUploadState = {
  status: 'idle',
  identifier: null,
  fileName: null,
  fileType: null,
  progress: 0,
  error: null,
};

export type UseFormFileUploadResult = {
  state: FormFileUploadState;
  upload: (file: File, chatInstanceId?: number | null) => Promise<string | null>;
  reset: () => void;
};

/**
 * Orchestrates the 4-step form file upload flow:
 * queue → start (STS creds) → browser-direct S3 upload → complete
 *
 * Returns the `identifier` string on success, which should be stored as the
 * form field value and sent with the form submission payload.
 */
export function useFormFileUpload(): UseFormFileUploadResult {
  const apiClient = useChatApiClient();
  const [state, setState] = useState<FormFileUploadState>(INITIAL_STATE);

  const reset = useCallback(() => {
    setState(INITIAL_STATE);
  }, []);

  const upload = useCallback(
    async (file: File, chatInstanceId: number | null = null): Promise<string | null> => {
      setState({
        status: 'queuing',
        identifier: null,
        fileName: file.name,
        fileType: file.type,
        progress: 0,
        error: null,
      });

      try {
        // Step 1: Queue the upload (create DB record + S3 key)
        const { fileUpload } = await apiClient.formFileUpload.queue({
          chatInstanceId,
          fileName: file.name,
          fileType: file.type || 'application/octet-stream',
          fileSize: file.size,
        });

        setState(prev => ({ ...prev, status: 'uploading' }));

        // Step 2: Start upload (get STS credentials)
        const { credentials } = await apiClient.formFileUpload.start(fileUpload.id);

        const s3Client = new S3({
          credentials: {
            accessKeyId: credentials.accessKeyId,
            secretAccessKey: credentials.secretAccessKey,
            sessionToken: credentials.sessionToken,
            expiration: credentials.expiration ? new Date(credentials.expiration) : undefined,
          },
          region: 'us-east-1',
          useAccelerateEndpoint: true,
        });

        // Step 3: Upload directly to S3
        const s3Upload = new Upload({
          client: s3Client,
          params: {
            Body: file,
            Bucket: fileUpload.bucket,
            ContentType: file.type || 'application/octet-stream',
            Key: fileUpload.s3Key,
            Metadata: {
              'group-id': String(fileUpload.groupId),
            },
            StorageClass: 'STANDARD_IA',
          },
        });

        s3Upload.on('httpUploadProgress', event => {
          if (event.total && event.total > 0) {
            setState(prev => ({
              ...prev,
              progress: Math.ceil(((event.loaded ?? 0) * 100) / event.total!),
            }));
          }
        });

        const result = await s3Upload.done();
        void result as CompleteMultipartUploadOutput;

        setState(prev => ({ ...prev, status: 'completing', progress: 100 }));

        // Step 4: Mark upload as complete
        await apiClient.formFileUpload.complete(fileUpload.id);

        setState(prev => ({
          ...prev,
          status: 'success',
          identifier: fileUpload.identifier,
        }));

        return fileUpload.identifier;
      } catch (err) {
        const message =
          err instanceof Error ? err.message : 'File upload failed. Please try again.';

        // Best-effort fail call — we don't await to avoid masking the original error
        setState(prev => ({ ...prev, status: 'error', error: message }));
        return null;
      }
    },
    [apiClient],
  );

  return { state, upload, reset };
}
